import { useMemo } from "react";

const useDisposition = (dispositionData, trendsData) => {
  const donutData = useMemo(() => {
    if (!dispositionData || dispositionData.length === 0) {
      return [];
    }

    const total = dispositionData.reduce((sum, item) => sum + item.total, 0);

    const getDispositionColor = (disposition, index) => {
      const colors = [
        "#6B7280", // Gray for null
        "#10B981", // Green for answered
        "#EF4444", // Red for no answer
        "#3B82F6", // Blue for voicemail
        "#F59E0B", // Amber for busy
        "#8B5CF6", // Purple for connected
        "#DC2626", // Dark red for DNC
        "#EC4899", // Pink
        "#06B6D4", // Cyan
      ];

      const colorMap = {
        null: "#6B7280", // Gray for null disposition
        "": "#6B7280", // Gray for empty disposition
        answered: "#10B981", // Green
        "Left voicemail": "#3B82F6", // Blue
        "No answer": "#EF4444", // Red
        Connected: "#8B5CF6", // Purple
        Busy: "#F59E0B", // Amber
        "DNC (Do Not Contact)": "#DC2626", // Dark red
      };

      return colorMap[disposition] || colors[index % colors.length];
    };

    return dispositionData.map((item, index) => {
      const displayName =
        item.disposition === null || item.disposition === "" ? "No Disposition" : item.disposition;

      return {
        name: displayName,
        value: item.total,
        percentage: ((item.total / total) * 100).toFixed(1),
        color: getDispositionColor(item.disposition, index),
      };
    });
  }, [dispositionData]);

  const lineChartData = useMemo(() => {
    if (!trendsData || trendsData.length === 0) {
      return [];
    }

    const allDates = new Set();
    const allDispositions = new Set();

    trendsData.forEach(({ DATE, DISPOSITION }) => {
      const date = new Date(DATE);
      const dateKey = date.toISOString().split("T")[0];
      allDates.add(dateKey);

      const dispositionKey = DISPOSITION === null || DISPOSITION === "" ? "No Disposition" : DISPOSITION;
      allDispositions.add(dispositionKey);
    });

    const dateMap = {};

    allDates.forEach((dateKey) => {
      const date = new Date(dateKey);
      const displayDate = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });

      dateMap[dateKey] = {
        date: displayDate,
        dateKey: dateKey,
      };

      allDispositions.forEach((disposition) => {
        dateMap[dateKey][disposition] = 0;
      });
    });

    trendsData.forEach(({ DATE, DISPOSITION, TOTAL }) => {
      const date = new Date(DATE);
      const dateKey = date.toISOString().split("T")[0];
      const dispositionKey = DISPOSITION === null || DISPOSITION === "" ? "No Disposition" : DISPOSITION;

      dateMap[dateKey][dispositionKey] = parseInt(TOTAL);
    });

    return Object.values(dateMap).sort((a, b) => new Date(a.dateKey) - new Date(b.dateKey));
  }, [trendsData]);

  const legendData = useMemo(() => {
    if (!dispositionData || dispositionData.length === 0) return [];

    const getDispositionColor = (disposition, index) => {
      const colors = [
        "#6B7280", // Gray for null/no disposition
        "#10B981", // Green for answered
        "#EF4444", // Red for no answer
        "#3B82F6", // Blue for voicemail
        "#F59E0B", // Amber for busy
        "#8B5CF6", // Purple for connected
        "#DC2626", // Dark red for DNC
        "#EC4899", // Pink
        "#06B6D4", // Cyan
      ];

      const colorMap = {
        null: "#6B7280", // Gray for null disposition
        "": "#6B7280", // Gray for empty disposition
        answered: "#10B981", // Green
        "Left voicemail": "#3B82F6", // Blue
        "No answer": "#EF4444", // Red
        Connected: "#8B5CF6", // Purple
        Busy: "#F59E0B", // Amber
        "DNC (Do Not Contact)": "#DC2626", // Dark red
      };

      return colorMap[disposition] || colors[index % colors.length];
    };

    return dispositionData.map((item, index) => ({
      name: item.disposition === null || item.disposition === "" ? "No Disposition" : item.disposition,
      color: getDispositionColor(item.disposition, index),
    }));
  }, [dispositionData]);

  const getYAxisDomain = useMemo(() => {
    if (!lineChartData || lineChartData.length === 0) return [0, 10];

    const allValues = lineChartData.flatMap((item) =>
      Object.keys(item)
        .filter((key) => key !== "date" && key !== "dateKey")
        .map((key) => item[key] || 0)
    );

    if (allValues.length === 0) return [0, 10];

    const max = Math.max(...allValues);
    const roundedMax = Math.ceil(max / 10) * 10;

    return [0, Math.max(roundedMax, 10)];
  }, [lineChartData]);

  return {
    donutData,
    lineChartData,
    legendData,
    yAxisDomain: getYAxisDomain,
  };
};

export default useDisposition;
